import { Injectable, EventEmitter, Output } from '@angular/core';
import { AbcCore, ConvertHelper, GUID } from '@barthauer/abc/core';
import {
  PersistObjectModel,
  TargetObjectData,
  PersistMode,
  ObjectKey,
  TargetColumnValue,
  QueryOutputMode,
  QueryObjectsModel,
  Query,
  PersistObjectsModel,
  QueryColumnSortOrder,
} from '@barthauer/abc/core/data';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import {
  CategorizedMtObjWorkTypesWithStates,
  MtObjectWork,
  MtObjWorkType,
} from '../../create-works-wizard/models/MtWork';
import { MtWorkGenerationInfo } from '../../create-works-wizard/models/MtWorkGenerationInfo';
import { WorkService } from '../../create-works-wizard/services/work.service';
import { WorkState } from '../../create-works-wizard/models/WorkWizardOptions';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { UserService } from '@barthauer/basys/core/authentication';

export interface DmlDocument {
  id: string;
  name: string;
}

export interface MaintenanceCycleData {
  cycleLength: number;
  cycleUnit: number;
  workTypeId: string;
  firstExecutionDate: Date | null;
  designation: string;
  execTime?: number;
  leadTime?: number;
}

export interface ObjectGroupGenerationResult {
  createdGroups: any[];
  editedGroups: any[];
  addedObjects: any[];
  removedObjects: any[];
  createdMaintenanceCycles: any[];
  failedMaintenanceCycles: any[];
}

export interface MaintenanceCycleEvaluationResult {
  evaluatedWorkTemplates: any[];
  evaluatedAnlagen: any[];
  generatedWorks: any[];
  anlagenWithExistingWorks: any[];
  anlagenWithoutDueMaintenanceCycle: any[];
}

export interface ProcessGroupOptions {
  mode: 'createNew' | 'editExisting' | 'evaluate';
  selectedAssetIds: string[];
  addedObjectsRawData: any[];
  designation: string;
  isMaintenanceCycleChecked: boolean;
  cycleLength: number;
  selectedCycleUnitId: number | null;
  selectedGroupTypeId: string | null;
  firstExecutionDate: Date | null;
  leadTime: number | null;
  selectedAnlageGroupId: string | null;
  alreadyAssignedIds: string[];
  originalAnlageDetailMap: Map<string, string>;
  groupInfos: any;
}

@Injectable({
  providedIn: 'root',
})
export class ObjectGroupService {
  @Output() generationFinished = new EventEmitter<ObjectGroupGenerationResult>();
  @Output() generationFailed = new EventEmitter<any>();
  @Output() evaluationFinished = new EventEmitter<MaintenanceCycleEvaluationResult>();
  @Output() evaluationFailed = new EventEmitter<any>();
  public evaluatedWorkTemplates: any;
  public evaluatedAnlagen: any;
  public resAsync: Promise<ResourceSet>;

  constructor(
    private abcCore: AbcCore,
    private workService: WorkService,
    private resourceService: ResourceManagerService,
    private userService: UserService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('opera-wizards');
  }

  public async createMtObjectGroup(
    designation: string,
    cycleLength: number | null,
    cycleUnit: number | null,
    workTypeId?: string,
    firstExecutionDate?: Date | null,
    leadTime?: number | null,
  ): Promise<string> {
    const groupId = GUID.newGUID();
    const groupInsert = new PersistObjectModel();
    const query = new TargetObjectData();

    query.Mode = PersistMode.Insert;
    query.ObjectKey = new ObjectKey();
    query.ObjectKey.ObjectType = 'MtObjectGroup';

    const targetColumns: TargetColumnValue[] = [
      { Name: 'Id', Value: groupId },
      { Name: 'Designation', Value: designation },
      { Name: 'CycleLength', Value: cycleLength },
      { Name: 'CycleUnit', Value: cycleUnit },
      { Name: 'LeadTime', Value: leadTime },
    ];

    if (workTypeId) {
      targetColumns.push({ Name: 'WorkTypeId', Value: workTypeId });
    }

    if (firstExecutionDate) {
      targetColumns.push({ Name: 'CycleStartDate', Value: firstExecutionDate });
    }

    if (leadTime) {
      targetColumns.push({ Name: 'LeadTime', Value: leadTime });
    }

    query.TargetColumns = targetColumns;
    groupInsert.Object = query;
    groupInsert.ObjectSpaceName = 'BaSYS.Complete';

    await this.abcCore.data.executePersistObjectQuery(groupInsert).toPromise();
    return groupId;
  }

  public async assignObjectsToGroup(groupId: string, objectIds: string[]): Promise<void> {
    for (const objectId of objectIds) {
      const model = new PersistObjectModel();
      const query = new TargetObjectData();

      query.Mode = PersistMode.Insert;
      query.ObjectKey = new ObjectKey();
      query.ObjectKey.ObjectType = 'MtObjectGroupDetail';
      query.TargetColumns = [
        { Name: 'ParentId', Value: groupId },
        { Name: 'ObjectId', Value: objectId },
      ];

      model.Object = query;
      model.ObjectSpaceName = 'BaSYS.Complete';

      await this.abcCore.data.executePersistObjectQuery(model).toPromise();
    }
  }

  public createObjectGroupAsDropDownListQuery(): Observable<ArrayBuffer> {
    const query: QueryObjectsModel = new QueryObjectsModel();
    const objectQuery: Query = new Query();

    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    objectQuery.ObjectType = 'MtObjectGroup';
    objectQuery.OPath = 'true';

    objectQuery.Columns = [
      { Name: 'Id', OPath: 'Id' },
      { Name: 'Designation', OPath: 'Designation' },
      { Name: 'CycleLength', OPath: 'CycleLength' },
      { Name: 'CycleUnit', OPath: 'CycleUnit' },
      { Name: 'WorkTypeId', OPath: 'WorkTypeId' },
      { Name: 'CycleStartDate', OPath: 'CycleStartDate' },
      { Name: 'LeadTime', OPath: 'LeadTime' },
    ];

    query.ObjectQueries = [objectQuery];

    return this.abcCore.data.executeReadObjectsQuery(query);
  }

  public async getAnlagenIdsForGroup(groupId: string): Promise<string[]> {
    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObjectGroupDetail';
    objectQuery.OPath = `ParentId = '${groupId}'`;
    objectQuery.Columns = [{ Name: 'ObjectId', OPath: 'ObjectId' }];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));

      if (!Array.isArray(result)) {
        console.warn('Expected array but got:', result);
        return [];
      }
      return result.map((row: any) => row.ObjectId);
    } catch (error) {
      console.error('Error while fetching assigned Anlage IDs:', error);
      return [];
    }
  }

  public async deleteObjectGroupAssignment(detailId: string): Promise<boolean> {
    const persistQuery = this.createAnlageGroupRemoveAssignmentQuery(detailId);

    try {
      await this.abcCore.data.executePersistObjectsQuery(persistQuery).toPromise();
      return true;
    } catch (err) {
      console.error('Error while removing AnlageGroupDetail:', err);
      return false;
    }
  }

  private createAnlageGroupRemoveAssignmentQuery(detailId: string): PersistObjectsModel {
    const queryModel = new PersistObjectsModel();
    const objQueries: TargetObjectData[] = [];

    const persistQuery = new TargetObjectData();
    persistQuery.Mode = PersistMode.Delete;
    persistQuery.ObjectKey = new ObjectKey();
    persistQuery.ObjectKey.ObjectType = 'MtObjectGroupDetail';
    persistQuery.ObjectKey.Id = detailId;

    objQueries.push(persistQuery);
    queryModel.Objects = [objQueries];

    return queryModel;
  }

  public async getAssignedAnlageDetails(groupId: string): Promise<{ Id: string; ObjectId: string }[]> {
    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObjectGroupDetail';
    objectQuery.OPath = `ParentId = '${groupId}'`;
    objectQuery.Columns = [
      { Name: 'Id', OPath: 'Id' },
      { Name: 'ObjectId', OPath: 'ObjectId' },
    ];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));
      return result as unknown as { Id: string; ObjectId: string }[];
    } catch (err) {
      console.error('Error in getAssignedAnlageDetails:', err);
      return [];
    }
  }

  private async getObjectsWithExistingCyclesForWorkType(objectIds: string[], workTypeId: string): Promise<Set<string>> {
    if (!objectIds || objectIds.length === 0) {
      return new Set();
    }

    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObjWorkType';
    objectQuery.OPath = `ParentId in (${objectIds.map((id) => `'${id}'`).join(',')}) And WorkTypeId = '${workTypeId}'`;
    objectQuery.Columns = [{ Name: 'ParentId', OPath: 'ParentId' }];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));
      if (Array.isArray(result)) {
        return new Set(result.map((row: any) => row.ParentId));
      }
      return new Set();
    } catch (error) {
      console.error('Error checking existing cycles:', error);
      return new Set();
    }
  }

  public async createMaintenanceCyclesForObjects(
    objectIds: string[],
    maintenanceData: MaintenanceCycleData,
  ): Promise<{ createdIds: string[]; failures: { objectId: string; error: any }[] }> {
    const createdIds: string[] = [];
    const failures: { objectId: string; error: any }[] = [];

    const objectsWithExistingCycles = await this.getObjectsWithExistingCyclesForWorkType(
      objectIds,
      maintenanceData.workTypeId,
    );

    for (const objectId of objectIds) {
      if (objectsWithExistingCycles.has(objectId)) {
        failures.push({
          objectId,
          error: {
            message: 'Wartungszyklus existiert bereits für diese Anlage',
          },
        });
        continue;
      }

      try {
        const workTypeId = await this.createMaintenanceCycleForObjectQuery(objectId, maintenanceData);
        createdIds.push(workTypeId);
      } catch (error) {
        failures.push({
          objectId,
          error: {
            message: 'Fehler beim Erstellen des Wartungszyklus',
          },
        });
      }
    }
    return { createdIds, failures };
  }

  public async createMaintenanceCycleForObjectQuery(
    objectId: string,
    maintenanceData: MaintenanceCycleData,
  ): Promise<string> {
    const workTypeId = GUID.newGUID();
    const model = new PersistObjectModel();
    const query = new TargetObjectData();

    query.Mode = PersistMode.Insert;
    query.ObjectKey = new ObjectKey();
    query.ObjectKey.ObjectType = 'MtObjWorkType';

    const objectDesignation = await this.getObjectDesignationObject(objectId);

    query.TargetColumns = [
      { Name: 'Id', Value: workTypeId },
      { Name: 'ParentId', Value: objectId },
      { Name: 'WorkTypeId', Value: maintenanceData.workTypeId },
      { Name: 'CycleLength', Value: maintenanceData.cycleLength },
      { Name: 'CycleUnit', Value: maintenanceData.cycleUnit },
      { Name: 'Designation', Value: maintenanceData.designation },
      { Name: 'Description', Value: `Wartungszyklus für ${objectDesignation}` },
      { Name: 'ExecFirst', Value: maintenanceData.firstExecutionDate },
      { Name: 'ExecTime', Value: maintenanceData.execTime },
      { Name: 'LeadTime', Value: maintenanceData.leadTime },
    ];

    model.Object = query;
    model.ObjectSpaceName = 'BaSYS.Complete';

    await this.abcCore.data.executePersistObjectQuery(model).toPromise();

    return workTypeId;
  }

  public async updateObjectGroupCycle(
    groupId: string,
    cycleLength?: number | null,
    cycleUnit?: number | null,
    cycleStartDate?: Date | null,
    leadTime?: number | null,
  ): Promise<void> {
    const model = new PersistObjectModel();
    const q = new TargetObjectData();

    q.Mode = PersistMode.Update;
    q.ObjectKey = new ObjectKey();
    q.ObjectKey.ObjectType = 'MtObjectGroup';
    q.ObjectKey.Id = groupId;

    q.TargetColumns = [
      { Name: 'CycleLength', Value: cycleLength },
      { Name: 'CycleUnit', Value: cycleUnit },
      { Name: 'CycleStartDate', Value: cycleStartDate },
      { Name: 'leadTime', Value: leadTime },
    ];

    model.Object = q;
    model.ObjectSpaceName = 'BaSYS.Complete';

    await firstValueFrom(this.abcCore.data.executePersistObjectQuery(model));
  }

  private async getObjectDesignationObject(objectId: string): Promise<string> {
    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObject';
    objectQuery.OPath = `Id = '${objectId}'`;
    objectQuery.Columns = [{ Name: 'Designation', OPath: 'Designation', SortOrder: QueryColumnSortOrder.Ascending }];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));
      if (Array.isArray(result) && result.length > 0) {
        return result[0].Designation || objectId;
      }
      return objectId;
    } catch (error) {
      console.error('Error getting object designation:', error);
      return objectId;
    }
  }

  public async deleteMaintenanceCyclesForObjects(objectIds: string[]): Promise<void> {
    for (const objectId of objectIds) {
      try {
        await this.deleteMaintenanceCyclesForObject(objectId);
      } catch (error) {
        console.error(`Failed to delete maintenance cycles for object ${objectId}:`, error);
      }
    }
  }

  public async deleteMaintenanceCyclesForObject(objectId: string): Promise<void> {
    const maintenanceCycleIds = await this.getMaintenanceCycleIdsForObjectQuery(objectId);

    if (maintenanceCycleIds.length === 0) {
      console.log(`No maintenance cycles found for object ${objectId}`);
      return;
    }

    // Delete each maintenance cycle
    for (const cycleId of maintenanceCycleIds) {
      await this.deleteMaintenanceCycle(cycleId);
    }
  }

  private async getMaintenanceCycleIdsForObjectQuery(objectId: string): Promise<string[]> {
    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObjWorkType';
    objectQuery.OPath = `ParentId = '${objectId}'`; // Find maintenance cycles for this object
    objectQuery.Columns = [{ Name: 'Id', OPath: 'Id' }];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));
      if (Array.isArray(result)) {
        return result.map((row: any) => row.Id);
      }
      return [];
    } catch (error) {
      console.error('Error getting maintenance cycle IDs:', error);
      return [];
    }
  }

  private async deleteMaintenanceCycle(cycleId: string): Promise<boolean> {
    const model = new PersistObjectModel();
    const query = new TargetObjectData();

    query.Mode = PersistMode.Delete;
    query.ObjectKey = new ObjectKey();
    query.ObjectKey.ObjectType = 'MtObjWorkType';
    query.ObjectKey.Id = cycleId;

    model.Object = query;
    model.ObjectSpaceName = 'BaSYS.Complete';

    try {
      await this.abcCore.data.executePersistObjectQuery(model).toPromise();
      return true;
    } catch (error) {
      console.error(`Error deleting maintenance cycle ${cycleId}:`, error);
      return false;
    }
  }

  public async getObjectDetailsByIds(objectIds: string[]): Promise<any[]> {
    if (!objectIds || objectIds.length === 0) {
      return [];
    }

    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObject';
    objectQuery.OPath = `Id in (${objectIds.map((id) => `'${id}'`).join(',')})`;
    objectQuery.Columns = [
      { Name: 'Id', OPath: 'Id' },
      { Name: 'Designation', OPath: 'Designation', SortOrder: QueryColumnSortOrder.Ascending },
      { Name: 'MasterObjectType', OPath: 'GeObjectType' }, // Fetching the numeric type
      {
        OPath: 'Status',
        Name: 'ObjectState',
      },
      { Name: 'Street', OPath: 'ref:IdRefStrasse' },
      { Name: 'District', OPath: 'ref:IdRefOrtsteil' },
      {
        Name: 'Subject',
        OPath: `CaseWhen(Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'GeoObjekt', 5, 'Straße', ref:Kind)`, //localization
      },
    ];

    query.ObjectQueries = [objectQuery];

    try {
      const result = await firstValueFrom(this.abcCore.data.executeReadObjectsQuery(query));
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Error getting object details by IDs:', error);
      return [];
    }
  }

  public async processGroupConfiguration(options: ProcessGroupOptions): Promise<void> {
    const protocolData: ObjectGroupGenerationResult = {
      createdGroups: [],
      editedGroups: [],
      addedObjects: [],
      removedObjects: [],
      createdMaintenanceCycles: [],
      failedMaintenanceCycles: [],
    };

    try {
      if (options.mode === 'createNew') {
        // ========== CREATE NEW GROUP ==========
        const cycleLengthToSend = options.isMaintenanceCycleChecked ? options.cycleLength : null;
        const cycleUnitToSend = options.isMaintenanceCycleChecked ? options.selectedCycleUnitId : null;
        const workTypeIdToSend = options.selectedGroupTypeId ?? undefined;
        const leadTimeToSend = options.isMaintenanceCycleChecked ? options.leadTime : null;

        const groupId = await this.createMtObjectGroup(
          options.designation,
          cycleLengthToSend,
          cycleUnitToSend,
          workTypeIdToSend,
          options.firstExecutionDate,
          leadTimeToSend,
        );

        protocolData.createdGroups.push({
          Id: groupId,
          Designation: options.designation,
          CycleLength: cycleLengthToSend,
          CycleUnitId: cycleUnitToSend,
          WorkTypeId: workTypeIdToSend,
          FirstExecutionDate: options.firstExecutionDate,
          LeadTime: leadTimeToSend + (await this.resAsync).get('PlanningLeadTimeUnit'),
        });

        if (options.selectedAssetIds.length > 0) {
          const workType = await firstValueFrom(this.workService.queryWorkType(workTypeIdToSend!));
          await this.assignObjectsToGroup(groupId, options.selectedAssetIds);
          const assignedObjects = await this.getObjectDetailsByIds(options.selectedAssetIds);
          protocolData.addedObjects.push(...assignedObjects);

          if (options.isMaintenanceCycleChecked) {
            const maintenanceData: MaintenanceCycleData = {
              cycleLength: options.cycleLength!,
              cycleUnit: options.selectedCycleUnitId!,
              workTypeId: options.selectedGroupTypeId!,
              firstExecutionDate: options.firstExecutionDate,
              designation: options.designation || 'Wartungszyklus (neu)',
              execTime: workType.execTime,
              leadTime: options.leadTime,
            };

            await this.createCyclesAndUpdateProtocol(
              options.selectedAssetIds,
              maintenanceData,
              protocolData,
              options.designation,
            );
          }
        }
      } else {
        // ========== EDIT EXISTING GROUP ==========
        const groupId = options.selectedAnlageGroupId!;

        protocolData.editedGroups.push({
          Id: groupId,
          Designation: options.groupInfos.Description,
          Intervall: options.cycleLength,
          CycleUnitId: options.selectedCycleUnitId,
          WorkTypeId: options.groupInfos.WorkTypeId,
          ErsteAusfuehrung: options.firstExecutionDate,
          LeadTime: options.leadTime + (await this.resAsync).get('PlanningLeadTimeUnit'),
        });

        const newIds = options.selectedAssetIds.filter((id) => !options.alreadyAssignedIds.includes(id));
        const removedIds = options.alreadyAssignedIds.filter((id) => !options.selectedAssetIds.includes(id));

        if (newIds.length > 0) {
          await this.assignObjectsToGroup(groupId, newIds);
          const addedObjects = await this.getObjectDetailsByIds(newIds);
          protocolData.addedObjects.push(...addedObjects);
        }

        if (removedIds.length > 0) {
          const detailIdsToRemove = removedIds
            .map((id) => options.originalAnlageDetailMap.get(id))
            .filter((id): id is string => !!id);
          for (const detailId of detailIdsToRemove) {
            await this.deleteObjectGroupAssignment(detailId);
          }
          const removedObjects = await this.getObjectDetailsByIds(removedIds);
          protocolData.removedObjects.push(...removedObjects);
          await this.deleteMaintenanceCyclesForObjects(removedIds);
        }

        const workTypeIdToUse = options.groupInfos?.WorkTypeId;

        if (
          options.isMaintenanceCycleChecked &&
          options.selectedCycleUnitId != null &&
          options.cycleLength &&
          workTypeIdToUse
        ) {
          const workType = await firstValueFrom(this.workService.queryWorkType(workTypeIdToUse));
          const maintenanceData: MaintenanceCycleData = {
            cycleLength: options.cycleLength,
            cycleUnit: options.selectedCycleUnitId,
            workTypeId: workTypeIdToUse.trim(),
            firstExecutionDate: options.firstExecutionDate,
            designation: options.groupInfos.Description || options.groupInfos.Id,
            execTime: workType.execTime,
            leadTime: options.leadTime,
          };

          const allSelectedIds = options.selectedAssetIds;

          if (allSelectedIds.length > 0) {
            await this.createCyclesAndUpdateProtocol(
              allSelectedIds,
              maintenanceData,
              protocolData,
              options.groupInfos.Description,
            );
          }
        } else if (
          newIds.length > 0 &&
          options.groupInfos.CycleLength != null &&
          options.groupInfos.CycleUnit != null &&
          options.groupInfos.WorkTypeId
        ) {
          const workType = await firstValueFrom(this.workService.queryWorkType(options.groupInfos.WorkTypeId));
          const maintenanceData: MaintenanceCycleData = {
            cycleLength: options.groupInfos.CycleLength,
            cycleUnit: options.groupInfos.CycleUnit,
            workTypeId: options.groupInfos.WorkTypeId.trim(),
            firstExecutionDate: options.firstExecutionDate || options.groupInfos.CycleStartDate,
            designation: options.groupInfos.Description || options.groupInfos.Id,
            execTime: workType.execTime,
            leadTime: options.leadTime,
          };

          await this.createCyclesAndUpdateProtocol(
            newIds,
            maintenanceData,
            protocolData,
            options.groupInfos.Description,
          );
        }
      }

      this.generationFinished.emit(protocolData);
    } catch (error) {
      console.error('Error in processGroupConfiguration:', error);
      this.generationFailed.emit(error);
    }
  }

  private async createCyclesAndUpdateProtocol(
    objectIds: string[],
    maintenanceData: MaintenanceCycleData,
    protocolData: ObjectGroupGenerationResult,
    groupDesignation: string,
  ): Promise<void> {
    const { failures } = await this.createMaintenanceCyclesForObjects(objectIds, maintenanceData);

    const objectsForCycles = await this.getObjectDetailsByIds(objectIds);
    const failedIds = new Set(failures.map((f) => f.objectId));
    const successObjects = objectsForCycles.filter((a) => !failedIds.has(a.Id));

    protocolData.createdMaintenanceCycles.push(
      ...successObjects.map((asset) => ({
        Id: GUID.newGUID(),
        Bezeichnung: groupDesignation,
        Anlage: asset.Designation,
        Intervall: maintenanceData.cycleLength,
        CycleUnitId: maintenanceData.cycleUnit,
        WorkTypeId: maintenanceData.workTypeId,
        ErsteAusfuehrung: maintenanceData.firstExecutionDate,
        MasterObjectType: asset.MasterObjectType,
      })),
    );

    if (failures.length) {
      const failedObjects = await this.getObjectDetailsByIds(failures.map((f) => f.objectId));
      const nameById = new Map(failedObjects.map((o) => [o.Id, o.Designation || o.Id]));

      protocolData.failedMaintenanceCycles.push(
        ...failures.map((f) => ({
          Id: GUID.newGUID(),
          Anlage: nameById.get(f.objectId) ?? f.objectId,
          WorkTypeId: maintenanceData.workTypeId,
          Bezeichnung: groupDesignation,
          Intervall: maintenanceData.cycleLength,
          CycleUnitId: maintenanceData.cycleUnit,
          ErsteAusfuehrung: maintenanceData.firstExecutionDate,
          ErrorMessage: String(f.error?.message || f.error || 'Unbekannter Fehler'),
        })),
      );

      protocolData.failedMaintenanceCycles = protocolData.failedMaintenanceCycles.map((work) => {
        const match = failedObjects.find((obj) => obj.Designation === work.Anlage);
        return {
          ...work,
          MasterObjectType: match ? match.MasterObjectType : null,
        };
      });
    }
  }

  public async existsCyclicWorksToBeGenerated(): Promise<boolean> {
    const query = await this.createMaintenanceCycleWorksQueryCore([], [], true, true, false, true);
    const result = await firstValueFrom(
      this.abcCore.data.executeReadObjectsQuery(query).pipe(
        map((arrayBuffer) => {
          const result = ConvertHelper.arrayBufferToObjectArray<MtObjWorkType>(arrayBuffer).filter(
            (t) => !t.HasExistingWorks,
          );
          return Array.isArray(result) && result.length > 0;
        }),
        catchError((err) => {
          console.error('Query failed:', err);
          return of(false);
        }),
      ),
    );

    return result;
  }

  public async getObjectsWithAvailableCyclicWorks(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
  ): Promise<MtObjectWork[]> {
    const categorized = await this.getCategorizedObjWorkTypes(worktypeIds, objectIds);
    return !!categorized ? categorized.ObjectsWithAvailableWorks : [];
  }

  public async getObjectsWithGenerableCyclicWorks(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
  ): Promise<MtObjectWork[]> {
    const categorized = await this.getCategorizedObjWorkTypes(worktypeIds, objectIds);
    return !!categorized ? categorized.ObjectsWithGenerableWorks : [];
  }

  public async getObjectsWithoutUpcomingCyclicWorks(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
  ): Promise<MtObjectWork[]> {
    const categorized = await this.getCategorizedObjWorkTypes(worktypeIds, objectIds);
    return !!categorized ? categorized.ObjectsWithoutUpcomingWorks : [];
  }

  public async evaluateObjWorkTypesWithProtocol(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
    workState: WorkState = WorkState.anstehend,
  ): Promise<void> {
    try {
      const evaluatedCategories = await this.evaluateObjWorkTypes(worktypeIds, objectIds, workState);
      const protocolData = this.createEvaluationProtocol(evaluatedCategories);
      this.evaluationFinished.emit(protocolData);
    } catch (error) {
      this.evaluationFailed.emit(error);
    }
  }

  private createEvaluationProtocol(
    evaluatedCategories: CategorizedMtObjWorkTypesWithStates | undefined,
  ): MaintenanceCycleEvaluationResult {
    const protocolData: MaintenanceCycleEvaluationResult = {
      evaluatedWorkTemplates: [],
      evaluatedAnlagen: [],
      generatedWorks: [],
      anlagenWithExistingWorks: [],
      anlagenWithoutDueMaintenanceCycle: [],
    };

    if (evaluatedCategories) {
      protocolData.evaluatedWorkTemplates.push(...this.evaluatedWorkTemplates);
      protocolData.evaluatedAnlagen.push(...this.evaluatedAnlagen);
      protocolData.generatedWorks.push(...evaluatedCategories.ObjectsWithGeneratedWorks);
      protocolData.anlagenWithExistingWorks.push(...evaluatedCategories.ObjectsWithAvailableWorks);
      protocolData.anlagenWithoutDueMaintenanceCycle.push(...evaluatedCategories.ObjectsWithoutUpcomingWorks);
    }

    return protocolData;
  }

  public async evaluateObjWorkTypes(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
    workState: WorkState = WorkState.anstehend,
  ): Promise<CategorizedMtObjWorkTypesWithStates | undefined> {
    const categorizeCycles = await this.getCategorizedObjWorkTypes(worktypeIds, objectIds);
    if (categorizeCycles && categorizeCycles.ObjectsWithGenerableWorks.length > 0) {
      const cycleForWorks = new Map<string, MtWorkGenerationInfo[]>();
      for (const cycle of categorizeCycles.ObjectsWithGenerableWorks) {
        if (!cycleForWorks.has(cycle.WorkTypeId)) cycleForWorks.set(cycle.WorkTypeId, []);
        const info: MtWorkGenerationInfo = this.getGenerationInfo(cycle);
        cycleForWorks.get(cycle.WorkTypeId)!.push(info);
      }

      const results = await this.workService.generateWorksAtMaintenanceCyclesEvaluation(cycleForWorks, workState);
      const objectsWithGeneratedWorks = categorizeCycles.ObjectsWithGenerableWorks.filter((cycle) =>
        results.some((result) => result.Success && result.WorkTypeId === cycle.WorkTypeId),
      );
      for (let gw of objectsWithGeneratedWorks) {
        gw.WorkState = workState;
        gw.WorkStateText = WorkState[workState];
      }

      const objectsWorkGenerationFailed = categorizeCycles.ObjectsWithGenerableWorks.filter((cycle) =>
        results.some((result) => !result.Success && result.WorkTypeId === cycle.WorkTypeId),
      );

      categorizeCycles.ObjectsWithGeneratedWorks = objectsWithGeneratedWorks;
      categorizeCycles.ObjectsWorkGenerationFailed = objectsWorkGenerationFailed;
    } else {
      console.log('No cycled works need to be generated.');
    }

    return categorizeCycles;
  }

  private getGenerationInfo(cycle: MtObjWorkType): MtWorkGenerationInfo {
    const info = new MtWorkGenerationInfo();
    info.MtObjectId = cycle.ObjectId;
    info.MtObjWorkTypeId = cycle.Id;
    info.EarliestWorkExecDate = cycle.EarliestExecutionDate;
    info.LatestWorkExecDate = cycle.LatestExecutionDate;
    info.ScheduledWorkExecDate = cycle.ScheduledExecutionDate;
    info.WorkExecTime = cycle.ExecTime;
    return info;
  }

  public async getCategorizedObjWorkTypes(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
  ): Promise<CategorizedMtObjWorkTypesWithStates | undefined> {
    const mtObjTypes: MtObjWorkType[] = await this.getObjWorkTypesWithGenerationStates(worktypeIds, objectIds);
    const mtObjTypesGenerable = mtObjTypes.filter(
      (t) => t.IsValidMtObjWorkType && !t.HasExistingWorks && t.IsValidEarliestExecutionDate,
    );
    const mtObjTypesWithAvlWorks = mtObjTypes.filter((t) => t.HasExistingWorks);
    const mtObjTypesWOUpcomingWorks = mtObjTypes.filter(
      (t) => !t.IsValidEarliestExecutionDate && t.IsValidMtObjWorkType,
    );
    const ret = new CategorizedMtObjWorkTypesWithStates();
    ret.ObjectsWithGenerableWorks = mtObjTypesGenerable;
    ret.ObjectsWithAvailableWorks = mtObjTypesWithAvlWorks;
    ret.ObjectsWithoutUpcomingWorks = mtObjTypesWOUpcomingWorks;
    return ret;
  }

  private async getObjWorkTypesWithGenerationStates(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
  ): Promise<MtObjWorkType[]> {
    const query = await this.createMaintenanceCycleWorksQueryCore(worktypeIds, objectIds);
    const result = await firstValueFrom(
      this.abcCore.data.executeReadObjectsQuery(query).pipe(
        map((arrayBuffer) => {
          return ConvertHelper.arrayBufferToObjectArray<MtObjWorkType>(arrayBuffer);
        }),
        catchError((err) => {
          console.error('Query failed:', err);
          return of([]);
        }),
      ),
    );

    return result;
  }

  private async createMaintenanceCycleWorksQueryCore(
    worktypeIds: string[] = [],
    objectIds: string[] = [],
    useIsValidEarliestExecutionDateAsFilter: boolean = false,
    useIsValidMtObjWorkTypeAsFilter: boolean = false,
    useHasExistingWorksAsFilter: boolean = false,
    filterOutInactiveWorkTypes: boolean = false,
  ): Promise<QueryObjectsModel> {
    const query = new QueryObjectsModel();
    query.ObjectSpaceName = 'BaSYS.Complete';
    query.OutputMode = QueryOutputMode.Json;

    let filter: string = 'MtObject.ParentId=$CurrentMunicipalityVariantId';
    const filterWorkTypes = this.idsToINList(worktypeIds);
    const filterObjectIds = this.idsToINList(objectIds);

    if (!!filterWorkTypes) {
      filter += ' And WorkTypeId in ' + filterWorkTypes;
    }

    if (!!filterObjectIds) {
      filter += ' And ParentId in ' + filterObjectIds;
    }

    if ((await this.userService.getServerVersion())?.substring(0, 6) >= '9.26.1' && filterOutInactiveWorkTypes) {
      filter += " And WorkType.State <> '1'";
    }

    const objectQuery: Query = new Query();
    objectQuery.ObjectType = 'MtObjWorkType';
    objectQuery.OPath = filter;
    objectQuery.Columns = [
      {
        OPath: 'Id',
        Name: 'Id',
      },
      {
        OPath: 'ParentId',
        Name: 'ObjectId',
      },
      {
        OPath: 'WorkTypeId',
        Name: 'WorkTypeId',
      },
      {
        OPath: 'MtObject.Designation',
        Name: 'Object',
        SortOrder: QueryColumnSortOrder.Ascending,
      },
      { OPath: 'MtObject.GeObjectType', Name: 'MasterObjectType' },
      {
        OPath: 'MtObject.Status',
        Name: 'ObjectState',
      },
      {
        Name: 'Subject',
        OPath: `CaseWhen(Mtobject.Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'GeoObjekt', 5, 'Straße', ref:MtObject.Kind)`, //localization
      },
      { OPath: 'ref:MtObject.IdRefStrasse', Name: 'Street' },
      { OPath: 'ref:MtObject.IdRefOrtsteil', Name: 'District' },
      {
        OPath: 'WorkType.Designation',
        Name: 'WorkType',
      },
      {
        OPath: 'Designation',
        Name: 'Designation',
      },
      {
        OPath: 'Description',
        Name: 'Description',
      },
      {
        OPath: 'CycleLength',
        Name: 'CycleLength',
      },
      {
        OPath: 'CycleUnit',
        Name: 'CycleUnit',
      },
      {
        OPath: 'ref:CycleUnit',
        Name: 'CycleUnitText',
      },
      {
        OPath: 'ExecFirst',
        Name: 'ExecFirst',
      },
      {
        OPath: 'ExecLast',
        Name: 'ExecLast',
      },
      {
        OPath: 'CaseNull(ExecTime, CaseNull(WorkType.ExecTime, 7))',
        Name: 'ExecTime',
      },
      {
        OPath: 'CaseNull(LeadTime, CaseNull(WorkType.LeadTime, 7))',
        Name: 'LeadTime',
      },
      {
        OPath: this.getExistingWorkStateExpression(),
        Name: 'WorkState',
      },
      {
        OPath: this.getExistingWorkStateTextExpression(),
        Name: 'WorkStateText',
      },
      {
        OPath: this.getEarliestExecutionDateExpression(),
        Name: 'EarliestExecutionDate',
      },
      {
        OPath: this.getEarliestExecutionDateExpression(), //the same as earliest
        Name: 'ScheduledExecutionDate',
      },
      {
        OPath: this.getLatestExecutionDateExpression(),
        Name: 'LatestExecutionDate',
      },
      {
        OPath: this.getIsValidEarliestExecutionDateExpression(),
        Name: 'IsValidEarliestExecutionDate',
        IsFilter: useIsValidEarliestExecutionDateAsFilter,
      },
      {
        OPath: this.getIsValidMtObjWorkTypeExpression(),
        Name: 'IsValidMtObjWorkType',
        IsFilter: useIsValidMtObjWorkTypeAsFilter,
      },
      {
        OPath: this.getHasExistingWorksExpression(),
        Name: 'HasExistingWorks',
        IsFilter: useHasExistingWorksAsFilter,
      },
    ];

    query.ObjectQueries = [objectQuery];
    return query;
  }

  private idsToINList(ids: string[]): string {
    if (ids && ids.length > 0) {
      return `('${ids.join("','")}')`;
    }

    return '';
  }

  private getEarliestExecutionDateExpression(): string {
    return `
    
    CaseWhen
      (
        CycleUnit
      ,
        0
      ,
        AddDays
        (
        GetDate(ExecFirst)
        ,
        Ceiling
        (
          ToFloat
          (
          Ceiling(GetTimeSpan(CurrentDate(), GetDate(ExecFirst)) / 7.0)
          /
          ToFloat(CycleLength)
          )
        ) * (CycleLength * 7)
        )
      ,
        1
      ,
        AddMonths
        (
        GetDate(ExecFirst)
        ,
        Ceiling
        (
          ToFloat(
          (GetYear(CurrentDate()) * 12 + GetMonth(CurrentDate()))
          -
          (GetYear(ExecFirst) * 12 + GetMonth(ExecFirst))
          +
          IIf(GetDay(CurrentDate()) > GetDay(ExecFirst), 1, 0)
          )
          /
          ToFloat(CycleLength)
        ) * CycleLength
        )
      ,
        2
      ,
        AddYears
        (
        GetDate(ExecFirst)
        ,
        Ceiling
        (
          ToFloat(
          GetYear(CurrentDate())
          -
          GetYear(ExecFirst)
          +
          IIf(GetDayOfYear(CurrentDate()) > GetDayOfYear(ExecFirst), 1, 0)
          )
          /
          ToFloat(CycleLength)
        ) * CycleLength
        )
      )
  `;
  }

  private getLatestExecutionDateExpression(): string {
    const expression =
      ` AddDays ( ` +
      this.getEarliestExecutionDateExpression() +
      `,  
      CaseNull(ExecTime, CaseNull(WorkType.ExecTime, 7)))`;

    return expression;
  }

  private getIsValidEarliestExecutionDateExpression(): string {
    const expression =
      this.getEarliestExecutionDateExpression() +
      `
      <=

      Min
      (
        AddDays
        (
          GetDate(CurrentDate())
        ,
          CaseNull(LeadTime, CaseNull(WorkType.LeadTime, 7))
        )
      ,
        GetDate(CaseNull(ExecLast, MaxDate()))
      )
    `;

    return expression;
  }

  private getIsValidMtObjWorkTypeExpression(): string {
    const expression = `
      CaseNull(CycleLength, 0) > 0 And 
      CycleUnit In (0, 1, 2) And
      Not(IsNull(ExecFirst)) And
      GetDate(ExecFirst) <= CurrentDate() And
      (IsNull(ExecLast) Or GetDate(ExecLast) > CurrentDate())
    `;

    return expression;
  }

  private getHasExistingWorksExpression(): string {
    return `Exists(` + this.getExistingWorksExpression() + `)`;
  }

  private getExistingWorksExpression(): string {
    const expression = `
      WorkType.Works[

        IdRefObjWorkType=^.^.Id 

        And

        Not (IsNull(ScheduledExecutionDate))

        And 

        IsBetween(
        GetTimeSpan(GetDate(ScheduledExecutionDate), 


          CaseWhen
              (
                ^.^.CycleUnit
              ,
                0
              ,
                AddDays
                (
                GetDate(^.^.ExecFirst)
                ,
                Ceiling
                (
                  ToFloat
                  (
                  Ceiling(GetTimeSpan(CurrentDate(), GetDate(^.^.ExecFirst)) / 7.0)
                  /
                  ToFloat(^.^.CycleLength)
                  )
                ) * (^.^.CycleLength * 7)
                )
              ,
                1
              ,
                AddMonths
                (
                GetDate(^.^.ExecFirst)
                ,
                Ceiling
                (
                  ToFloat(
                  (GetYear(CurrentDate()) * 12 + GetMonth(CurrentDate()))
                  -
                  (GetYear(^.^.ExecFirst) * 12 + GetMonth(^.^.ExecFirst))
                  +
                  IIf(GetDay(CurrentDate()) > GetDay(^.^.ExecFirst), 1, 0)
                  )
                  /
                  ToFloat(^.^.CycleLength)
                ) * ^.^.CycleLength
                )
              ,
                2
              ,
                AddYears
                (
                GetDate(^.^.ExecFirst)
                ,
                Ceiling
                (
                  ToFloat(
                  GetYear(CurrentDate())
                  -
                  GetYear(^.^.ExecFirst)
                  +
                  IIf(GetDayOfYear(CurrentDate()) > GetDayOfYear(^.^.ExecFirst), 1, 0)
                  )
                  /
                  ToFloat(^.^.CycleLength)
                ) * ^.^.CycleLength
                )
              )
          )

        , 0, CaseNull(^.^.ExecTime, CaseNull(^.ExecTime, 7)))]
    `;

    return expression;
  }

  private getExistingWorkStateExpression(): string {
    return this.getExistingWorksExpression() + '.Min(State)'; //If any, there is only one record
  }

  private getExistingWorkStateTextExpression(): string {
    return this.getExistingWorksExpression() + '.Min(ref:State)';
  }
}
